import { Inject, Injectable, InjectionToken, inject } from '@angular/core';
import { isNativeShell } from '../_helpers/native-shell';
import { ReplaySubject, Subject, Subscription, timer } from 'rxjs';
import { NGXLogger } from 'ngx-logger';
import { IMessage, MessageType } from '../_interface/crossAppMessaging';
declare var hybridWebViewHost: any | undefined;

@Injectable({
  providedIn: 'root'
})
export class CrossAppMessagingService {
  private subscriptions: { [key: string]: Subject<string> } = {};

  // "Sticky" poruke: native ih šalje kao odgovor na appLoaded (sa root AppComponent-a),
  // pre nego što se lazy feature-modul pretplati. Za njih koristimo ReplaySubject(1) da
  // poslednja vrednost bude reprodukovana i pretplatniku koji stigne kasnije.
  private readonly stickyMessages = new Set<string>(['shellAppConfig']);

  private _callbacks: {
    [key: string]: {
      resolve: (value: any) => void,
      reject: (err: any) => void,
      timer: Subscription,
      returnJSON: boolean
    }
  } = {};

  _logger?: NGXLogger
  private get logger() {
    if (this._logger == null) {
      this._logger = globalThis.AppInjector.get(NGXLogger);
    }

    return this._logger;
  }

  constructor(
  ) {

    if (window.chrome?.webview) {
      window.chrome.webview.addEventListener('message', (e: any) => {
        // e.data is already the object you sent with PostWebMessageAsJson
        ((window as any).onNativeMessage || function () { })(e.data);
      });
    }

    // Optional: expose onNativeMessage if your native code also uses eval() on Android/iOS
    (window as any).onNativeMessage = (msg: any) => {
      // forward to your app-level handler
      if (typeof (window as any).crossAppMessage === 'function') {
        (window as any).crossAppMessage(msg);
      } else {
        console.log('From native:', msg);
      }
    };


    (window as any).crossAppMessage = (message: any) => {
      try {
        var jMsg = this.stringToJsonObject(message);
      }
      catch (e) {
        this.logger.error("Error parsing message", message);
        return
      }
      var requestID = jMsg.requestID;

      if (requestID) {
        this.logger.trace("Received response for request " + requestID, jMsg);

        var promise = this._callbacks[requestID];

        if (promise) {
          delete this._callbacks[requestID];

          promise.timer.unsubscribe();

          var msg = jMsg.data;
          if (promise.returnJSON) {
            if (msg != null) {
              //    msg = msg.replace(/'/g, '"');
              try {
                msg = this.stringToJsonObject(msg);
              } catch (e) {
                this.logger.error("Error parsing message", msg);
              }
            }
          }
          promise.resolve(msg);
        }
      }
      else {
        this.logger.trace("Received message " + jMsg.type, jMsg);

        var type = jMsg.type;
        if (this.stickyMessages.has(type)) {
          // buferuj vrednost i kad još nema pretplatnika (ReplaySubject je kreiran unapred)
          this.ensureSubject(type).next(jMsg.data);
        }
        else {
          this.subscriptions[type]?.next(jMsg.data);
        }
      }
    }
  }

  stringToJsonObject(input: string) {
    if (typeof input !== 'string') return input;          // already an object
    let s = input.trim();

    // 1) Try plain JSON
    try { return JSON.parse(s); } catch { }

    // 2) Try double-encoded JSON: e.g. "\"{...}\""
    try { return JSON.parse(JSON.parse(s)); } catch { }

    // 3) Try URL-decoded then parse (useful if it came via ?data=<...>)
    try { return JSON.parse(decodeURIComponent(s)); } catch { }

    // 4) Strip wrapping quotes and unescape \" then parse
    if (s.startsWith('"') && s.endsWith('"')) {
      try { return JSON.parse(s.slice(1, -1).replace(/\\"/g, '"')); } catch { }
    }

    throw new Error('Could not parse JSON payload');
  }


  onMessage(messageType: string): Subject<string> {
    return this.ensureSubject(messageType);
  }

  private ensureSubject(messageType: string): Subject<string> {
    if (!this.subscriptions[messageType]) {
      this.subscriptions[messageType] = this.stickyMessages.has(messageType)
        ? new ReplaySubject<string>(1)
        : new Subject<string>();
    }

    return this.subscriptions[messageType];
  }

  sendRawMessage(message: IMessage, waitForResponse: boolean = false, waitTimeout: number = 60000, retrunJSON = false) {

    if (!message.requestID && waitForResponse) {
      message.requestID = Math.random().toString();
    };

    var m = JSON.stringify(message);

    // console.log(new Date, message.action, message.requestID);


    this.sendMessage(0, m);

    if (waitForResponse) {
      return new Promise<any>((resolve, reject) => {
        this._callbacks[message.requestID!] = {
          resolve: resolve,
          reject: reject,
          timer: timer(waitTimeout).subscribe(() => {
            this.logger.warn("Request timeout - rejecting", message.requestID, message.action);
            reject()
          }),
          returnJSON: retrunJSON
        };

      });
    }
    else {
      return Promise.resolve();
    }


  }

  sendInvokeMessage(methodName: string, paramValues: any[]) {
    if (typeof paramValues !== 'undefined') {
      if (!Array.isArray(paramValues)) {
        paramValues = [paramValues];
      }
      for (var i = 0; i < paramValues.length; i++) {
        paramValues[i] = JSON.stringify(paramValues[i]);
      }
    }

    this.sendMessage(1, JSON.stringify({ "MethodName": methodName, "ParamValues": paramValues }));
  }

  private sendMessage(messageType: MessageType, messageContent: string) {
    if (!isNativeShell()) {
      return;
    }

    var message = JSON.stringify({ "MessageType": messageType, "MessageContent": messageContent });
    var w = window as any;
    if (w.chrome && w.chrome.webview) {
      // Windows WebView2
      w.chrome.webview.postMessage(message);
    }
    else if (w.webkit && w.webkit.messageHandlers && w.webkit.messageHandlers.webwindowinterop) {
      // iOS and MacCatalyst WKWebView
      w.webkit.messageHandlers.webwindowinterop.postMessage(message);
    }
    else if (typeof hybridWebViewHost !== 'undefined') {
      // Android WebView
      hybridWebViewHost.sendMessage(message);
    }
  }

}
